/**
 * The `/` menu (`discovery/ui-reference/docs/INDEX.md` §3).
 *
 * Measured: 224 wide, placeholder *"Type to search list"*, a `Sidekick` row on its own, a
 * `Text` section with `Normal text · Large title # · Medium title ## · Small title ### ·
 * Quote >`, then `More options`, which reveals the rest. Rows are 210 × 32.
 *
 * `Sidekick` is monday's AI and is rendered **disabled with its tooltip**, never omitted: the
 * shape of the menu is part of the muscle memory.
 */
import { useMemo, useState } from 'react';
import { useOverlayDismiss } from '@elizmonday/ui';
import type { DocBlockType } from '@elizmonday/shared';

export interface SlashEntry {
  type: DocBlockType;
  label: string;
  shortcut: string;
  /** `false` puts the entry behind "More options", as monday does. */
  primary: boolean;
}

/** The catalogue, in monday's own order. */
export const SLASH_ENTRIES: readonly SlashEntry[] = [
  { type: 'normal text', label: 'Normal text', shortcut: '', primary: true },
  { type: 'large title', label: 'Large title', shortcut: '#', primary: true },
  { type: 'medium title', label: 'Medium title', shortcut: '##', primary: true },
  { type: 'small title', label: 'Small title', shortcut: '###', primary: true },
  { type: 'quote', label: 'Quote', shortcut: '>', primary: true },
  { type: 'bulleted list', label: 'Bulleted list', shortcut: '-', primary: false },
  { type: 'numbered list', label: 'Numbered list', shortcut: '1.', primary: false },
  { type: 'check list', label: 'Checklist', shortcut: '[]', primary: false },
  { type: 'code', label: 'Code', shortcut: '```', primary: false },
  { type: 'divider', label: 'Divider', shortcut: '---', primary: false },
];

export function SlashMenu({
  x,
  y,
  onPick,
  onClose,
}: {
  x: number;
  y: number;
  onPick(type: DocBlockType): void;
  onClose(): void;
}) {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(false);

  /*
   * P-013: Escape closed the menu only while the search box had focus, and left focus on
   * `<body>`. The hook owns the key now — from anywhere — and returns focus to the block the
   * `/` was typed in.
   */
  useOverlayDismiss({ onClose });

  const entries = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const pool = needle === '' && !expanded ? SLASH_ENTRIES.filter((entry) => entry.primary) : SLASH_ENTRIES;
    return needle === '' ? pool : pool.filter((entry) => entry.label.toLowerCase().includes(needle));
  }, [query, expanded]);

  return (
    <div
      className="ez-doc-slash"
      role="menu"
      aria-label="Insert a block"
      data-testid="doc-slash-menu"
      style={{ left: x, top: y }}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <input
        className="ez-doc-slash__search"
        placeholder="Type to search list"
        data-testid="doc-slash-search"
        autoFocus
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && entries[0]) onPick(entries[0].type);
        }}
      />

      <button
        type="button"
        className="ez-doc-slash__row"
        role="menuitem"
        disabled
        data-testid="doc-slash-sidekick"
        title="Sidekick is monday's AI; it is not part of this product"
      >
        <span>✦ Sidekick</span>
      </button>

      <p className="ez-doc-slash__section">Text</p>
      {entries.map((entry) => (
        <button
          key={entry.type}
          type="button"
          role="menuitem"
          className="ez-doc-slash__row"
          data-testid={`doc-slash-${entry.type.replace(/ /g, '-')}`}
          onClick={() => onPick(entry.type)}
        >
          <span>{entry.label}</span>
          {entry.shortcut === '' ? null : <code>{entry.shortcut}</code>}
        </button>
      ))}

      {expanded ? null : (
        <button
          type="button"
          className="ez-doc-slash__more"
          data-testid="doc-slash-more"
          onClick={() => setExpanded(true)}
        >
          More options
        </button>
      )}
    </div>
  );
}
